import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import homeHeroImg1 from "../assets/home-hero44.png";
import homeHeroImg2 from "../assets/home-hero66.png";
import homeHeroImg3 from "../assets/home-hero88.png";
import ROUTES from "../routes";
import { EXTERNAL_LINKS } from "../constants/externalLinks";

const slides = [homeHeroImg1, homeHeroImg2, homeHeroImg3];

export default function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="hero-wrapper">
      {/* Background images — CSS crossfade (compositor thread, no scroll jank) */}
      {slides.map((img, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${img})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: i === currentSlide ? 1 : 0,
            transition: "opacity 1.5s ease-in-out",
            willChange: "opacity",
            transform: "translateZ(0)",
          }}
        />
      ))}

      {/* Cinematic gradient overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.42) 50%, rgba(4,18,38,0.88) 100%)",
          zIndex: 1,
        }}
      />

      {/* Decorative rings */}
      <div className="hero-ring hero-ring-1" />
      <div className="hero-ring hero-ring-2" />
      <div className="hero-ring hero-ring-3" />

      {/* Main content */}
      <div
        style={{
          position: "relative",
          zIndex: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "0 1.5rem 80px",
        }}
      >
        {/* Live badge */}
        <motion.div
          initial={{ opacity: 0, y: -24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.65 }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "10px",
            background: "rgba(0,0,0,0.45)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            border: "1px solid rgba(109,199,68,0.55)",
            borderRadius: "999px",
            padding: "9px 20px",
            marginBottom: "1.8rem",
            color: "#fff",
            fontSize: "clamp(0.7rem, 2vw, 0.83rem)",
            fontWeight: 600,
            letterSpacing: "1.4px",
            textTransform: "uppercase",
            fontFamily: "'Montserrat', 'Segoe UI', sans-serif",
            textShadow: "0 1px 4px rgba(0,0,0,0.6)",
            maxWidth: "90vw",
            whiteSpace: "nowrap",
          }}
        >
          <span className="hero-pulse-dot" />
          Open Every Saturday &nbsp;•&nbsp; 9AM – 4PM
        </motion.div>

        {/* Main title */}
        <motion.h1
          initial={{ opacity: 0, y: 44 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.9, ease: "easeOut" }}
          style={{
            fontSize: "clamp(3rem, 9vw, 6.5rem)",
            fontWeight: 900,
            color: "#fff",
            fontFamily: "'Montserrat', 'Segoe UI', sans-serif",
            lineHeight: 1.0,
            marginBottom: "1.2rem",
            maxWidth: "880px",
            textShadow: "0 4px 40px rgba(0,0,0,0.3)",
            letterSpacing: "-2px",
          }}
        >
          St. John's
          <br />
          <span className="hero-gradient-text">Farmers' Market</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          style={{
            fontSize: "clamp(1rem, 2.4vw, 1.3rem)",
            color: "rgba(255,255,255,0.83)",
            maxWidth: "560px",
            marginBottom: "3rem",
            lineHeight: 1.7,
            fontWeight: 400,
          }}
        >
          Fresh produce, handcrafted goods, live music, and genuine community —
          every Saturday in the heart of Newfoundland.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.6 }}
          style={{
            display: "flex",
            gap: "16px",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <a href={ROUTES.BECOME_A_VENDOR} className="hero-btn-primary">
            Become a Vendor
          </a>
          <a href={ROUTES.SEE_EVENTS} className="hero-btn-ghost">
            See Upcoming Events
          </a>
        </motion.div>

        {/* Social links */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.5 }}
          style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "1.75rem", justifyContent: "center", flexWrap: "wrap" }}
        >
          <span style={{ color: "rgba(255,255,255,0.38)", fontSize: "0.68rem", letterSpacing: "2px", textTransform: "uppercase", fontFamily: "'Montserrat', sans-serif", userSelect: "none" }}>
            Follow us
          </span>
          <a
            href={EXTERNAL_LINKS.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="hero-social-btn"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
            </svg>
            Facebook
          </a>
          <a
            href={EXTERNAL_LINKS.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="hero-social-btn"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
              <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
            </svg>
            Instagram
          </a>
        </motion.div>

        {/* Slide indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          style={{
            position: "absolute",
            bottom: "90px",
            display: "flex",
            gap: "8px",
            alignItems: "center",
          }}
        >
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              aria-label={`Slide ${i + 1}`}
              style={{
                width: i === currentSlide ? "34px" : "8px",
                height: "8px",
                borderRadius: "999px",
                background:
                  i === currentSlide
                    ? "#6dc744"
                    : "rgba(255,255,255,0.35)",
                border: "none",
                cursor: "pointer",
                transition: "all 0.35s ease",
                padding: 0,
              }}
            />
          ))}
        </motion.div>

        {/* Scroll hint */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.7, 0.7, 0] }}
          transition={{
            delay: 2,
            duration: 2.5,
            repeat: Infinity,
            repeatDelay: 0.5,
          }}
          style={{
            position: "absolute",
            bottom: "36px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "5px",
            color: "rgba(255,255,255,0.55)",
            fontSize: "0.68rem",
            letterSpacing: "2.5px",
            textTransform: "uppercase",
            fontFamily: "'Montserrat', sans-serif",
          }}
        >
          <span>Scroll</span>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              d="M9 3v12M4 10l5 5 5-5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </motion.div>
      </div>

      {/* Bottom wave — transitions into marquee/red section */}
      <div
        style={{
          position: "absolute",
          bottom: -1,
          left: 0,
          right: 0,
          zIndex: 4,
          lineHeight: 0,
        }}
      >
        <svg
          viewBox="0 0 1440 72"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          style={{ width: "100%", display: "block", height: "72px" }}
        >
          <path
            d="M0 72V34C200 64 460 74 720 48C980 22 1220 44 1440 34V72H0Z"
            fill="#c6252d"
          />
        </svg>
      </div>

      <style>{`
        .hero-wrapper {
          position: relative;
          height: 100vh;
          height: 100svh;
          min-height: 620px;
          overflow: hidden;
        }
        .hero-gradient-text {
          background: linear-gradient(90deg, #6dc744, #90db55, #4a8c25, #6dc744);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-size: 220% auto;
          animation: hero-shimmer 4s linear infinite;
          display: inline-block;
        }
        .hero-pulse-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          background: #6dc744;
          display: inline-block;
          flex-shrink: 0;
          animation: hero-pulse 2s ease-in-out infinite;
        }
        .hero-ring {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
          z-index: 2;
        }
        .hero-ring-1 {
          top: 10%; left: 4%;
          width: clamp(160px, 22vw, 280px);
          height: clamp(160px, 22vw, 280px);
          border: 1px solid rgba(255,255,255,0.07);
        }
        .hero-ring-2 {
          top: 16%; left: 9%;
          width: clamp(100px, 13vw, 170px);
          height: clamp(100px, 13vw, 170px);
          border: 1px solid rgba(109,199,68,0.1);
        }
        .hero-ring-3 {
          bottom: 22%; right: 5%;
          width: clamp(140px, 18vw, 230px);
          height: clamp(140px, 18vw, 230px);
          border: 1px solid rgba(109,199,68,0.09);
        }
        .hero-btn-primary {
          background: linear-gradient(90deg, #2d6a14, #4a8c25);
          color: #fff;
          padding: 0.88em 2.4em;
          border-radius: 999px;
          text-decoration: none;
          font-weight: 700;
          font-size: 1rem;
          font-family: 'Montserrat', 'Segoe UI', sans-serif;
          letter-spacing: 0.3px;
          box-shadow: 0 6px 26px rgba(74,140,37,0.38);
          transition: transform 0.2s, box-shadow 0.2s;
          display: inline-block;
        }
        .hero-btn-primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 34px rgba(74,140,37,0.52);
        }
        .hero-btn-ghost {
          background: rgba(255,255,255,0.1);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          color: #fff;
          padding: 0.88em 2.4em;
          border-radius: 999px;
          text-decoration: none;
          font-weight: 600;
          font-size: 1rem;
          font-family: 'Montserrat', 'Segoe UI', sans-serif;
          letter-spacing: 0.3px;
          border: 1.5px solid rgba(255,255,255,0.32);
          transition: background 0.2s, border-color 0.2s;
          display: inline-block;
        }
        .hero-btn-ghost:hover {
          background: rgba(255,255,255,0.2);
          border-color: rgba(255,255,255,0.6);
        }
        @keyframes hero-shimmer {
          0%   { background-position: 0% center; }
          100% { background-position: 220% center; }
        }
        @keyframes hero-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.45; transform: scale(0.75); }
        }
        .hero-social-btn {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          background: rgba(255,255,255,0.08);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          border: 1px solid rgba(255,255,255,0.18);
          border-radius: 999px;
          padding: 0.42rem 1.05rem;
          color: rgba(255,255,255,0.78);
          font-size: 0.8rem;
          font-weight: 600;
          font-family: 'Montserrat', 'Segoe UI', sans-serif;
          letter-spacing: 0.3px;
          text-decoration: none;
          transition: background 0.2s, border-color 0.2s, color 0.2s;
        }
        .hero-social-btn:hover {
          background: rgba(255,255,255,0.18);
          border-color: rgba(255,255,255,0.45);
          color: #fff;
        }
        @media (max-width: 600px) {
          .hero-btn-primary, .hero-btn-ghost {
            width: 100%;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
}
